import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Search, X, Menu, Plus, MessageSquare } from "lucide-react";
import { AuthContext } from "../../auth/context/AuthContext";
import { getAuthenticatedAxios } from "../../../utils/api";
import { formatRelativeTime, getDateGroupLabel } from "../../../shared/utils/date";
import {
  getHistoryCreditsLabel,
  getHistorySearchModeLabel,
} from "../utils/historySearchMetadata";

function ChatHistorySidebar({
  isOpen,
  setIsOpen,
  onLoadSearch,
  onNewSession,
  isMobile,
  currentSearchId,
  refreshKey,
}) {
  const { user } = useContext(AuthContext);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const historyLimit = 50;
  const sidebarRef = useRef(null);
  const backdropRef = useRef(null);
  const lastRefreshKeyRef = useRef(refreshKey);

  // Load search history when the sidebar opens or user changes.
  useEffect(() => {
    if (!user || !isOpen) return;
    loadSearchHistory();
  }, [user, isOpen]);

  useEffect(() => {
    if (!user || !isOpen) return;
    if (refreshKey === lastRefreshKeyRef.current) return;
    lastRefreshKeyRef.current = refreshKey;
    loadSearchHistory();
  }, [refreshKey, user, isOpen]);

  // Handle escape key and click outside for mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      const handleEscape = (e) => {
        if (e.key === "Escape") {
          setIsOpen(false);
        }
      };

      const handleClickOutside = (e) => {
        if (
          sidebarRef.current &&
          !sidebarRef.current.contains(e.target) &&
          !e.target.closest(".sidebar-toggle")
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);

      // Trap focus
      const focusableElements = sidebarRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements?.length > 0) {
        focusableElements[0].focus();
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isMobile, isOpen, setIsOpen]);

  const loadSearchHistory = async () => {
    setLoading(true);
    try {
      const authAxios = getAuthenticatedAxios();
      const response = await authAxios.get(
        `/api/v1/search-history?per_page=${historyLimit}`,
      );
      setSearches(response.data.searches || []);
    } catch (error) {
      console.error("Failed to load search history:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSearches = searches.filter((search) => {
    if (!searchTerm) return true;
    const query = search.query || "";
    return query.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Group searches by date
  const locale = i18n.language === "ja" ? "ja-JP" : "en-US";

  const groupedSearches = filteredSearches.reduce((groups, search) => {
    const groupKey = getDateGroupLabel({
      dateString: search.created_at,
      t,
      locale,
    });

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(search);
    return groups;
  }, {});

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          {isOpen && (
            <h2 className="text-sm font-semibold text-gray-900 transition-opacity duration-200">
              {t("searchHistory.title")}
            </h2>
          )}
        </div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors sidebar-toggle"
          aria-expanded={isOpen}
          aria-label={isOpen ? t("common.collapse") : t("common.expand")}
        >
          {isOpen ? (
            <X className="h-4 w-4 text-gray-600" />
          ) : (
            <Menu className="h-4 w-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* New Search Button */}
      <div className="p-2 border-b border-gray-200">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onNewSession) {
              onNewSession(e);
            } else {
              window.location.reload();
            }
          }}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#d3e3fd] hover:bg-[#c0d7fc] text-gray-800 transition-all duration-200 ${
            !isOpen ? "justify-center" : ""
          }`}
        >
          <Plus className="h-3.5 w-3.5 flex-shrink-0" />
          {isOpen && (
            <span className="text-xs font-medium transition-opacity duration-200">
              {t("app.newSession")}
            </span>
          )}
        </button>
      </div>

      {/* Search Box */}
      {isOpen && (
        <div className="p-2 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder={t("searchHistory.filterPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search History List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            {isOpen && (
              <span className="text-xs text-gray-500">
                {t("searchHistory.loadingHistory")}
              </span>
            )}
          </div>
        ) : filteredSearches.length === 0 ? (
          <div className="text-center py-6">
            {isOpen && (
              <p className="text-xs text-gray-500">
                {searchTerm
                  ? t("searchHistory.noMatchingSearches")
                  : t("searchHistory.noSearches")}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="py-2">
              {Object.entries(groupedSearches).map(([date, searches]) => (
                <div key={date}>
                  {isOpen && (
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      {date}
                    </div>
                  )}
                  {searches.map((search) => (
                    <div key={search.id} className="group relative">
                      {(() => {
                        const metadataParts = [
                          formatRelativeTime({
                            dateString: search.created_at,
                            t,
                            locale,
                          }),
                          getHistorySearchModeLabel({
                            searchMode: search.search_mode,
                            t,
                          }),
                          getHistoryCreditsLabel({
                            chargedCredits: search.charged_credits,
                            t,
                          }),
                          t("searchHistory.papersFound", {
                            count: search.result_count || 0,
                          }),
                        ].filter(Boolean);

                        return (
                      <button
                        onClick={() => onLoadSearch(search)}
                        className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 transition-all duration-200 ${
                          !isOpen ? "justify-center" : ""
                        } ${
                          currentSearchId === search.id
                            ? "bg-[#d3e3fd] border-l-2 border-teal-500"
                            : ""
                        }`}
                        title={search.query}
                      >
                        {isOpen && (
                          <>
                            <MessageSquare
                              className={`h-3.5 w-3.5 flex-shrink-0 ${
                                currentSearchId === search.id
                                  ? "text-teal-600"
                                  : "text-gray-400"
                              }`}
                            />
                            <div className="flex-1 text-left min-w-0">
                              <p
                                className={`text-xs truncate ${
                                  currentSearchId === search.id
                                    ? "text-teal-700 font-medium"
                                    : "text-gray-700"
                                }`}
                              >
                                {search.query}
                              </p>
                              <p
                                className={`text-[10px] ${
                                  currentSearchId === search.id
                                    ? "text-teal-600"
                                    : "text-gray-500"
                                }`}
                              >
                                {metadataParts.join(" • ")}
                              </p>
                            </div>
                          </>
                        )}
                      </button>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {isOpen && (
              <div className="px-3 pb-3 text-[10px] text-gray-500 space-y-1">
                <div>
                  {t("searchHistory.showingLatest", {
                    count: Math.min(historyLimit, searches.length),
                  })}
                </div>
                <div>
                  <span>{t("searchHistory.moreHistoryHint")} </span>
                  <button
                    onClick={() => navigate("/search-history")}
                    className="text-teal-600 hover:text-teal-700 underline"
                  >
                    {t("searchHistory.moreHistoryLink")}
                  </button>
                  <span>{t("searchHistory.moreHistorySuffix")}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  // Desktop sidebar
  if (!isMobile) {
    return (
      <aside
        ref={sidebarRef}
        className={`${
          isOpen ? "w-64" : "w-12"
        } h-full bg-[#e9eef6] border-r border-gray-200 flex flex-col transition-all duration-200 ease-in-out`}
        style={{
          transitionProperty: "width",
        }}
        aria-label={t("searchHistory.title")}
      >
        {sidebarContent}
      </aside>
    );
  }

  // Mobile drawer
  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          ref={backdropRef}
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-200"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <aside
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full w-64 bg-[#e9eef6] z-50 transform transition-transform duration-200 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label={t("searchHistory.title")}
        aria-modal={isMobile}
        role={isMobile ? "dialog" : undefined}
      >
        <div className="h-full flex flex-col">{sidebarContent}</div>
      </aside>
    </>
  );
}

export default ChatHistorySidebar;
